import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Route, Router, RouterStateSnapshot, UrlSegment } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import {
  MobileStateResponse,
  MobileStateService,
  ProdEligReqFieldsInterface,
  ProdEligResponseInterface,
  ProductEligibilityService,
  ProductPostIntroMapping,
  VerificationStatusService,
} from '@brightside-web/desktop/data-access/shared';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ProductVerificationGuard {
  sub = new Subscription();
  productEligibilityResp: ProdEligResponseInterface | undefined;
  nonRequiredFields = ['middle_name', 'address2'];

  constructor(
    private verificationStatus: VerificationStatusService,
    private router: Router,
    private prodElgSvc: ProductEligibilityService,
    private mobileStateService: MobileStateService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return of(true);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.mobileStateService.get(true).pipe(
      switchMap((ms) => {
        if (ms.spendingAccount || ms.savingsAccount || ms.externalSavings) {
          return of(true);
        } else if (this.productEligibilityResp) {
          return of(this.canClientActivate(route, state));
        } else {
          return this.prodElgSvc.getProductEligibility().pipe(
            map((response) => {
              this.productEligibilityResp = response;
              return this.canClientActivate(route, state);
            })
          );
        }
      })
    );
  }

  /** Call when product eligibility response must be refetched (e.g. dev menu profession toggle). */
  clearStoredEligibilityResponse(): void {
    this.productEligibilityResp = undefined;
  }

  canClientActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (Object.keys(ProductPostIntroMapping).includes(state.url) && !route.queryParams.fromFormSubmission) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const productType = ProductPostIntroMapping[state.url];
      const productFields = this.productEligibilityResp?.products.find(
        (product) => product.name.toLowerCase() === productType.toLowerCase()
      );
      if (productFields && productFields.required_fields && productFields.required_fields.length > 0) {
        const shouldContinueArray = [];
        productFields.required_fields.forEach((field) => {
          if (!field.not_replayable) {
            if (this.prodElgSvc.testReqValueForEmpty(field)) {
              shouldContinueArray.push(field);
            }
          }
          if (field.sub_fields) {
            field.sub_fields.forEach((subField) => {
              if (!subField.not_replayable) {
                if (this.prodElgSvc.testReqValueForEmpty(subField)) {
                  shouldContinueArray.push(subField);
                }
              }
            });
          }
        });
        if (shouldContinueArray.length > 0) {
          this.router.navigate([`/verification/identity/${productType}`]);
          return false;
        }
      }
    }
    return true;
  }
}
